import React, { useState } from "react";
import { KeyRound, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";

export function ChangePasswordDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setCur(""); setNext(""); setConfirm(""); };

  const submit = async () => {
    if (next.length < 8) return toast.error("New password must be at least 8 characters.");
    if (next !== confirm) return toast.error("New passwords don't match.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed successfully.");
      reset();
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="h-4 w-4 text-[#4338CA]" /> Change password</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1 block normal-case tracking-normal text-slate-600">Current password</Label>
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cp-current" autoComplete="current-password" />
          </div>
          <div>
            <Label className="mb-1 block normal-case tracking-normal text-slate-600">New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new" autoComplete="new-password" />
            <p className="mt-1 text-xs text-slate-400">At least 8 characters.</p>
          </div>
          <div>
            <Label className="mb-1 block normal-case tracking-normal text-slate-600">Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm" autoComplete="new-password" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !cur || !next || !confirm} data-testid="cp-submit" className="bg-[#4338CA] hover:bg-[#3730A3]">
            {saving ? "Saving…" : "Update password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ChangePasswordCard() {
  const { user } = useAuth();
  if (user?.auth_type === "phone") return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-5" data-testid="change-password-card">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500"><KeyRound className="h-4.5 w-4.5" /></span>
        <div>
          <p className="text-sm font-semibold text-slate-800">Account security</p>
          <p className="text-xs text-slate-500">Update the password you use to sign in to Servantage.</p>
        </div>
      </div>
      <ChangePasswordDialog trigger={<Button variant="outline" data-testid="open-change-password"><KeyRound className="mr-1.5 h-4 w-4" /> Change password</Button>} />
    </div>
  );
}
